/**
 * Account Aggregator Auth Key Drawer
 *
 * Collects expiry duration and triggers auth key generation.
 */

import React, { useEffect, useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay, Button, InputField } from 'equal-ds-ui';
import type { AccountAggregatorAuthKeyDrawerProps } from '../../../types/account-aggregator';

export const AccountAggregatorAuthKeyDrawer: React.FC<AccountAggregatorAuthKeyDrawerProps> = ({
  isOpen,
  aggregator,
  isSubmitting,
  onSubmit,
  onClose,
}) => {
  const [days, setDays] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setDays('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen || !aggregator) {
    return null;
  }

  const handleSubmit = async () => {
    const trimmed = days.trim();
    const parsed = Number(trimmed);
    if (!trimmed || Number.isNaN(parsed) || parsed <= 0) {
      setError('Enter a valid positive number of days.');
      return;
    }
    setError(null);
    await onSubmit(parsed);
  };

  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="420px">
        <DrawerHeader className="border-b border-border-default pb-4" onClose={onClose}>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Generate auth key</h2>
            <p className="text-sm text-text-secondary mt-1">
              AA ID: {aggregator.account_aggregator_id}
            </p>
          </div>
        </DrawerHeader>
        <DrawerContent className="p-6 space-y-4 overflow-y-auto flex-1">
          <InputField
            label="Expires in (days)"
            type="number"
            min={1}
            value={days}
            onChange={(event) => setDays(event.target.value)}
            disabled={isSubmitting}
            error={error || undefined}
            placeholder="Enter number of days"
          />
        </DrawerContent>
        <div className="px-6 py-4 border-t border-border-default flex justify-end gap-3 bg-background-secondary flex-shrink-0">
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Generating...' : 'Generate'}
          </Button>
        </div>
      </Drawer>
    </>
  );
};
